import type { UserType } from '@kanbu/schema';
import { createUserSchema, updateUserSchema } from '@kanbu/schema/contracts';
import { Role } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { toast } from '@utima/ui';
import { memo } from 'react';
import { z } from 'zod';

import { Alert } from '@/components/Alert';
import { Card } from '@/components/card/Card';
import { MetaFields } from '@/components/fields/MetaFields';
import { EntityCRUDForm } from '@/components/form/EntityCRUDForm';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { useEnumTranslations, useUser } from '@/hooks';
import { trpc } from '@/services/trpc';

import { useUserMutation } from '../useUserMutation';

export type UserFormProps = {
  data?: UserType;
};

export const UserForm = memo(function UserForm({ data }: UserFormProps) {
  const { t } = useLingui();
  const { user: currentUser, currentOrganizationId } = useUser();
  const translations = useEnumTranslations();
  const {
    create: { mutateAsync: create },
    remove: { mutateAsync: remove },
    update: { mutateAsync: update },
  } = useUserMutation();

  const organizationId = currentOrganizationId;

  const { data: activeSubscription, isLoading: isLoadingSub } =
    trpc.subscriptions.getActive.useQuery(
      { organizationId: organizationId! },
      { enabled: !!organizationId },
    );

  const { data: orgUsers, isLoading: isLoadingUsers } =
    trpc.users.findAll.useQuery(
      { where: { organizations: { id: organizationId } } },
      { enabled: !!organizationId },
    );
  const currentUserCount = orgUsers?.meta.total ?? 0;

  const maxUsersLimit =
    activeSubscription?.maxUsers === 0
      ? Number.MAX_SAFE_INTEGER
      : (activeSubscription?.maxUsers ?? 3);
  const isOverLimit = currentUserCount >= maxUsersLimit;
  const canBypassLimit = currentUser?.role === Role.SuperAdmin;

  const { schema: detailsSchema } = useFieldsSchema(
    [
      {
        name: 'email',
        label: t`Email`,
        required: true,
        disabled: !!data,
        parser: (value: unknown) =>
          typeof value === 'string' ? value.toLowerCase() : value,
      },
      {
        name: 'firstName',
        label: t`First name`,
      },
      {
        name: 'lastName',
        label: t`Last name`,
      },
      {
        name: 'role',
        type: 'select',
        uiControl: 'hidden',
        defaultValue: Role.Admin,
      },
    ],
    [t, currentUser?.role, translations.role, translations.roleWOSuperAdmin],
  );

  const { schema: operatorSchema } = useFieldsSchema(
    [
      {
        name: 'isActiveOperator',
        label: t`Active operator`,
        tooltip: t`Should this contact be displayed in relevant chats as a contact person`,
        type: 'checkbox',
        showOptional: false,
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            (formState?.values as any)?.isActiveOperator,
          $fields: [
            {
              name: 'contactEmail',
              label: t`Contact email`,
              tooltip: t`Alternative email (if empty, the registration email will be used)`,
              showOptional: false,
            },
            {
              name: 'phone',
              label: t`Phone`,
              type: 'text',
              showOptional: false,
            },
            {
              name: 'operatorSpecialization',
              label: t`Specialization`,
              type: 'text',
              tooltip: t`Area of expertise for this operator`,
              showOptional: false,
              uiControl: 'textarea',
            },
            {
              name: 'operatorDisplayText',
              label: t`Display text`,
              tooltip: t`Text content displayed with the operator contact`,
              type: 'richtext',
              showOptional: false,
            },
          ],
        },
      },
    ],
    [t],
  );

  return (
    <EntityCRUDForm
      initialData={data}
      createSchema={createUserSchema}
      updateSchema={updateUserSchema.extend({
        email: z.string().email().max(255),
      })}
      onCreate={create}
      onSubmitError={error => {
        if (
          typeof error === 'object' &&
          error &&
          'data' in error &&
          typeof error.data === 'object' &&
          error.data &&
          'code' in error.data &&
          error.data.code === 'CONFLICT'
        ) {
          toast.error(t`User already exists`, {
            description: t`Please choose a different email address.`,
          });

          return;
        }

        if (
          typeof error === 'object' &&
          error &&
          'message' in error &&
          typeof error.message === 'string' &&
          error.message.startsWith('User limit')
        ) {
          toast.error(t`User Limit Reached`, {
            description: error.message,
          });

          return;
        }

        toast.error(t`Failed to save user`, {
          description:
            error instanceof Error
              ? error.message
              : t`An unexpected error occurred.`,
        });
      }}
      onUpdate={values => {
        const { email, ...restValues } = values;

        return update(restValues);
      }}
      onRemove={remove}
      basePath='/users'
      idParamName='userId'
    >
      <EntityCRUDForm.Col>
        <Card title={t`Contact details`}>
          <SchemaFields schema={detailsSchema} />
        </Card>
      </EntityCRUDForm.Col>
      <EntityCRUDForm.Col>
        <Card title={t`Operator settings`}>
          <SchemaFields schema={operatorSchema} />
        </Card>
      </EntityCRUDForm.Col>
      {!data &&
        isOverLimit &&
        canBypassLimit &&
        !isLoadingSub &&
        !isLoadingUsers && (
          <Alert
            variant='warning'
            title={t`User Limit Reached`}
            className='mb-4 self-start'
          >
            <Trans>
              This organization has reached its user limit ({maxUsersLimit}). As
              a Super Admin, you can proceed with adding this user, bypassing
              the limit.
            </Trans>
          </Alert>
        )}

      <EntityCRUDForm.Col>
        <MetaFields data={data} />
      </EntityCRUDForm.Col>
    </EntityCRUDForm>
  );
});
